import type { CSSProperties } from 'react';
import { ColorType, getAccentColor, getColorValue } from '@/theme/colors';
import { pxToRem } from '@/theme/spacing';
import { getRadiusCssValue, getRadiusValue } from '@/theme/radius';
import { type BorderGradient, buildGradientString, GradientPreset, getGradientPreset } from '@/theme/gradients';
import type { CcontainerProps } from './Ccontainer.types';
import type { PresetConfig } from './CcontainerPresets';

const DISABLED_USER_SELECT = 'none';
const NO_BORDER = 'none';
const FLEX_DISPLAY: NonNullable<CcontainerProps['display']> = 'flex';
const INLINE_FLEX_DISPLAY: NonNullable<CcontainerProps['display']> = 'inline-flex';
const POINTER_CURSOR = 'pointer';
const TRANSPARENT_TAP_HIGHLIGHT_COLOR = 'transparent';
const INHERITED_BORDER_RADIUS = 'inherit';
const GRADIENT_MASK_COMPOSITE = 'xor';
const EXCLUDED_MASK_COMPOSITE = 'exclude';
const TOUCH_ACTION_MANIPULATION = 'manipulation';
const BOX_SIZING_BORDER_BOX = 'border-box';
const RELATIVE_POSITION: CSSProperties['position'] = 'relative';
const CONTENT_LAYER_WIDTH = '100%';
const CONTENT_LAYER_HEIGHT = '100%';
const LOADING_OPACITY_TRANSITION = 'opacity 600ms ease-in-out';
const GLOW_OPACITY_MULTIPLIER = 0.12;
const GLOW_VERTICAL_OFFSET_MULTIPLIER = 2;
const GLOW_BLUR_MULTIPLIER = 4;
const GLOW_SPREAD_MULTIPLIER = 0.2;
const UPPER_GLOSS_OPACITY_MULTIPLIER = 0.12;
const UPPER_GLOSS_X_MULTIPLIER = 0.7;
const UPPER_GLOSS_Y_MULTIPLIER = 0.9;
const UPPER_GLOSS_BLUR_MULTIPLIER = 1.1;
const LOWER_GLOSS_OPACITY = 0.06;
const LOWER_GLOSS_X_MULTIPLIER = -0.5;
const LOWER_GLOSS_Y_MULTIPLIER = -0.4;
const LOWER_GLOSS_BLUR_MULTIPLIER = 0.5;
const TRANSITION_PROPERTIES = [
  'background-color',
  'background-image',
  'box-shadow',
  'border',
  'border-radius',
  'padding',
  'opacity',
] as const;
const LOADING_BORDER_WIDTH_PX = 2;
const LOADING_BORDER_WHITE_OPACITY = 0.62;
const BACKGROUND_LAYER_SEPARATOR = ', ';

type FlexStyle = Pick<CSSProperties, 'display' | 'alignItems' | 'justifyContent' | 'flexDirection' | 'gap'>;
const PADDING_CONFLICT_LONGHAND_KEYS = [
  'paddingTop',
  'paddingRight',
  'paddingBottom',
  'paddingLeft',
] as const;
type PaddingConflictLonghandKey = (typeof PADDING_CONFLICT_LONGHAND_KEYS)[number];
const BORDER_CONFLICT_LONGHAND_KEYS = [
  'borderWidth',
  'borderStyle',
  'borderColor',
] as const;
type BorderConflictLonghandKey = (typeof BORDER_CONFLICT_LONGHAND_KEYS)[number];

interface ResolveContainerStyleValuesInput {
  presetConfig: PresetConfig;
  radius?: CcontainerProps['radius'];
  borderColor?: CcontainerProps['borderColor'];
  borderGradient?: CcontainerProps['borderGradient'];
  borderOpacity?: CcontainerProps['borderOpacity'];
  borderWidth?: CcontainerProps['borderWidth'];
  backgroundColor?: CcontainerProps['backgroundColor'];
  backgroundGradient?: CcontainerProps['backgroundGradient'];
  backgroundOpacity?: CcontainerProps['backgroundOpacity'];
  backdropBlur: number;
  padding?: CcontainerProps['padding'];
  width?: CcontainerProps['width'];
  height?: CcontainerProps['height'];
  glow: number;
  boxShadow?: CcontainerProps['boxShadow'];
  disableHoverShadow: boolean;
  flexDirection?: CcontainerProps['flexDirection'];
  overflow?: CcontainerProps['overflow'];
  isHovered: boolean;
}

interface ContainerStyleValues {
  finalRadiusValue: number;
  finalBorderColor?: CcontainerProps['borderColor'];
  finalBorderGradient?: BorderGradient;
  finalBorderOpacity?: number;
  finalBorderWidth: number;
  finalBackgroundColor?: CcontainerProps['backgroundColor'];
  finalBackgroundGradient?: BorderGradient;
  finalBackgroundImage?: string;
  finalBackgroundOpacity?: number;
  finalBackdropBlur: number;
  finalPadding: number | string;
  finalGlow: number;
  finalBoxShadow?: string;
  finalWidth?: number | string;
  finalHeight?: number | string;
  finalFlexDirection?: CcontainerProps['flexDirection'];
  finalOverflow?: CcontainerProps['overflow'];
}

interface BuildContainerStyleInput {
  values: ContainerStyleValues;
  display: NonNullable<CcontainerProps['display']>;
  alignItems?: CcontainerProps['alignItems'];
  justifyContent?: CcontainerProps['justifyContent'];
  gap?: CcontainerProps['gap'];
  glowColor: CcontainerProps['glowColor'];
  glossy: number;
  innerBorder: boolean;
  useGradientBorder: boolean;
  useGradientBackground: boolean;
  hasGlowLayer: boolean;
  isLoadingActive: boolean;
  smooth: boolean;
  animationDuration: number;
  onClick?: CcontainerProps['onClick'];
  clickable: boolean;
  style?: CSSProperties;
}

interface BuildContentWrapperStyleInput {
  display: NonNullable<CcontainerProps['display']>;
  finalFlexDirection?: CcontainerProps['flexDirection'];
  alignItems?: CcontainerProps['alignItems'];
  justifyContent?: CcontainerProps['justifyContent'];
  gap?: CcontainerProps['gap'];
  externalContentWrapperStyle?: CSSProperties;
  isLoadingActive: boolean;
  loadingOpacity: number;
}

interface BuildGradientBorderStyleInput {
  useGradientBorder: boolean;
  finalBorderGradient?: BorderGradient;
  finalBorderWidth: number;
}

interface BuildLoadingBorderStyleInput {
  isLoadingActive: boolean;
  gradientAngle: number;
  borderRevealProgress: number;
  endStopOpacity: number;
}

function resolveGradient(gradient?: BorderGradient | GradientPreset): BorderGradient | undefined {
  if (!gradient) {
    return undefined;
  }

  if (typeof gradient === 'string') {
    return getGradientPreset(gradient);
  }

  return gradient;
}

export function resolveContainerStyleValues(input: ResolveContainerStyleValuesInput): ContainerStyleValues {
  return {
    finalRadiusValue: resolveFinalRadiusValue(input),
    finalBorderColor: resolvePresetValue(input.borderColor, input.presetConfig.borderColor),
    finalBorderGradient: resolveGradient(resolvePresetValue(input.borderGradient, input.presetConfig.borderGradient)),
    finalBorderOpacity: resolvePresetValue(input.borderOpacity, input.presetConfig.borderOpacity),
    finalBorderWidth: resolvePresetValueWithDefault(input.borderWidth, input.presetConfig.borderWidth, 0),
    finalBackgroundColor: resolvePresetValue(input.backgroundColor, input.presetConfig.backgroundColor),
    finalBackgroundGradient: resolveGradient(resolvePresetValue(input.backgroundGradient, input.presetConfig.backgroundGradient)),
    finalBackgroundImage: input.presetConfig.backgroundImage,
    finalBackgroundOpacity: resolvePresetValue(input.backgroundOpacity, input.presetConfig.backgroundOpacity),
    finalBackdropBlur: resolvePresetValueWithDefault(input.backdropBlur, input.presetConfig.backdropBlur, 0),
    finalPadding: resolvePresetValueWithDefault(input.padding, input.presetConfig.padding, 0),
    finalGlow: resolvePresetValueWithDefault(input.glow, input.presetConfig.glow, 0),
    finalBoxShadow: resolveFinalBoxShadow(input),
    finalWidth: resolvePresetValue(input.width, input.presetConfig.width),
    finalHeight: resolvePresetValue(input.height, input.presetConfig.height),
    finalFlexDirection: resolvePresetValue(input.flexDirection, input.presetConfig.flexDirection),
    finalOverflow: resolvePresetValue(input.overflow, input.presetConfig.overflow),
  };
}

export function buildContainerStyle(input: BuildContainerStyleInput): CSSProperties {
  const { values } = input;
  const flexStyle = buildFlexStyle({
    display: input.display,
    alignItems: input.alignItems,
    justifyContent: input.justifyContent,
    flexDirection: values.finalFlexDirection,
    gap: input.gap,
  });

  const containerStyle: CSSProperties = {
    position: resolveRootPosition(input),
    display: input.display,
    ...flexStyle,
    borderRadius: getRadiusCssValue(values.finalRadiusValue),
    border: buildBorder({ input, values }),
    backgroundColor: buildBackgroundColor({
      useGradientBackground: input.useGradientBackground,
      finalBackgroundColor: values.finalBackgroundColor,
      finalBackgroundOpacity: values.finalBackgroundOpacity,
    }),
    backgroundImage: buildBackgroundImage({
      useGradientBackground: input.useGradientBackground,
      finalBackgroundGradient: values.finalBackgroundGradient,
      finalBackgroundImage: values.finalBackgroundImage,
    }),
    backdropFilter: buildBackdropFilter(values.finalBackdropBlur),
    WebkitBackdropFilter: buildBackdropFilter(values.finalBackdropBlur),
    padding: resolveCssLength(values.finalPadding),
    width: resolveCssLength(values.finalWidth),
    height: resolveCssLength(values.finalHeight),
    boxSizing: BOX_SIZING_BORDER_BOX,
    boxShadow: buildBoxShadow({ input, values }),
    transition: buildTransition({
      smooth: input.smooth,
      animationDuration: input.animationDuration,
    }),
    ...buildClickableStyle(input),
    overflow: values.finalOverflow,
    ...input.style,
  };

  return normalizeBorderShorthand(normalizePaddingShorthand(containerStyle));
}

export function buildGradientBorderStyle(input: BuildGradientBorderStyleInput): CSSProperties | undefined {
  if (!input.useGradientBorder || !input.finalBorderGradient) {
    return undefined;
  }

  return buildMaskedBorderLayerStyle({
    padding: `${input.finalBorderWidth}px`,
    backgroundImage: buildGradientString(input.finalBorderGradient),
    zIndex: 0,
  });
}

export function buildLoadingBorderStyle(input: BuildLoadingBorderStyleInput): CSSProperties | undefined {
  if (!input.isLoadingActive) {
    return undefined;
  }

  const accentOpacity = input.borderRevealProgress;
  const whiteOpacity = input.borderRevealProgress * LOADING_BORDER_WHITE_OPACITY;
  const endOpacity = input.endStopOpacity;
  const gradient =
    `linear-gradient(${input.gradientAngle}deg, ` +
    `rgba(102,254,193,${accentOpacity.toFixed(2)}) 0%, ` +
    `rgba(255,255,255,${whiteOpacity.toFixed(2)}) 24%, ` +
    `rgba(255,255,255,${endOpacity.toFixed(2)}) 50%)`;

  return buildMaskedBorderLayerStyle({
    padding: `${LOADING_BORDER_WIDTH_PX}px`,
    backgroundImage: gradient,
    zIndex: 10,
  });
}

export function buildContentWrapperStyle(input: BuildContentWrapperStyleInput): CSSProperties {
  const baseStyle: CSSProperties = {
    ...buildFlexStyle({
      display: resolveContentWrapperDisplay(input.display),
      alignItems: input.alignItems,
      justifyContent: input.justifyContent,
      flexDirection: input.finalFlexDirection,
      gap: input.gap,
    }),
    width: CONTENT_LAYER_WIDTH,
    height: CONTENT_LAYER_HEIGHT,
    ...input.externalContentWrapperStyle,
  };

  if (!input.isLoadingActive) {
    return baseStyle;
  }

  return {
    ...baseStyle,
    opacity: input.loadingOpacity,
    transition: LOADING_OPACITY_TRANSITION,
  };
}

function resolveContentWrapperDisplay(
  display: NonNullable<CcontainerProps['display']>,
): NonNullable<CcontainerProps['display']> {
  return display === INLINE_FLEX_DISPLAY ? FLEX_DISPLAY : display;
}

function buildFlexStyle(input: {
  display: NonNullable<CcontainerProps['display']>;
  alignItems?: CcontainerProps['alignItems'];
  justifyContent?: CcontainerProps['justifyContent'];
  flexDirection?: CcontainerProps['flexDirection'];
  gap?: CcontainerProps['gap'];
}): FlexStyle {
  if (!isFlexDisplay(input.display)) {
    return {
      display: undefined,
      alignItems: undefined,
      justifyContent: undefined,
      flexDirection: undefined,
      gap: undefined,
    };
  }

  return {
    display: input.display,
    alignItems: input.alignItems,
    justifyContent: input.justifyContent,
    flexDirection: input.flexDirection,
    gap: input.gap == null ? undefined : resolveCssLength(input.gap),
  };
}

function resolveFinalRadiusValue(input: ResolveContainerStyleValuesInput): number {
  const finalRadius = resolvePresetValueWithDefault(input.radius, input.presetConfig.radius, 0);

  return typeof finalRadius === 'number' ? finalRadius : getRadiusValue(finalRadius);
}

function resolvePresetValue<T>(explicitValue: T | undefined, presetValue: T | undefined): T | undefined {
  return explicitValue ?? presetValue;
}

function resolvePresetValueWithDefault<T>(
  explicitValue: T | undefined,
  presetValue: T | undefined,
  defaultValue: T,
): T {
  return explicitValue ?? presetValue ?? defaultValue;
}

function resolveFinalBoxShadow(input: ResolveContainerStyleValuesInput): string | undefined {
  if (input.boxShadow != null) {
    return input.boxShadow;
  }

  return resolvePresetBoxShadow(input);
}

function resolvePresetBoxShadow(input: ResolveContainerStyleValuesInput): string | undefined {
  if (!shouldUseHoverBoxShadow(input)) {
    return input.presetConfig.boxShadow;
  }

  return resolvePresetValue(input.presetConfig.hoverBoxShadow, input.presetConfig.boxShadow);
}

function shouldUseHoverBoxShadow(input: ResolveContainerStyleValuesInput): boolean {
  return input.isHovered && !input.disableHoverShadow;
}

function isFlexDisplay(display: NonNullable<CcontainerProps['display']>): boolean {
  return display === FLEX_DISPLAY || display === INLINE_FLEX_DISPLAY;
}

function resolveCssLength(value?: number | string): string | undefined {
  if (value == null) {
    return undefined;
  }

  return typeof value === 'number' ? pxToRem(value) : value;
}

function buildBorder(input: {
  input: BuildContainerStyleInput;
  values: ContainerStyleValues;
}): CSSProperties['border'] {
  const { values } = input;
  const finalBorderColor = values.finalBorderColor;

  if (!finalBorderColor || !shouldBuildCssBorder(input)) {
    return NO_BORDER;
  }

  return `${values.finalBorderWidth}px solid ${getColorValue(
    finalBorderColor,
    values.finalBorderOpacity ?? 1,
  )}`;
}

function shouldBuildCssBorder(input: {
  input: BuildContainerStyleInput;
  values: ContainerStyleValues;
}): boolean {
  return [
    !input.input.useGradientBorder,
    !input.input.innerBorder,
    input.values.finalBorderColor != null,
  ].every(Boolean);
}

function buildBackgroundColor(input: {
  useGradientBackground: boolean;
  finalBackgroundColor?: CcontainerProps['backgroundColor'];
  finalBackgroundOpacity?: number;
}): CSSProperties['backgroundColor'] {
  if (input.useGradientBackground || !input.finalBackgroundColor) {
    return undefined;
  }

  return getColorValue(input.finalBackgroundColor, input.finalBackgroundOpacity ?? 1);
}

function buildBackgroundImage(input: {
  useGradientBackground: boolean;
  finalBackgroundGradient?: BorderGradient;
  finalBackgroundImage?: string;
}): CSSProperties['backgroundImage'] {
  return joinBackgroundImageLayers([
    input.finalBackgroundImage,
    buildGradientBackgroundImage(input),
  ]);
}

function buildGradientBackgroundImage(input: {
  useGradientBackground: boolean;
  finalBackgroundGradient?: BorderGradient;
}): string | undefined {
  if (!input.useGradientBackground || input.finalBackgroundGradient == null) {
    return undefined;
  }

  return buildGradientString(input.finalBackgroundGradient);
}

function joinBackgroundImageLayers(layers: ReadonlyArray<string | null | undefined>): CSSProperties['backgroundImage'] {
  const presentLayers = layers.filter(isPresentString);

  if (presentLayers.length === 0) {
    return undefined;
  }

  return presentLayers.join(BACKGROUND_LAYER_SEPARATOR);
}

function isPresentCssValue(value: unknown): boolean {
  return value != null && value !== '';
}

function buildBackdropFilter(backdropBlur: number): string | undefined {
  return backdropBlur > 0 ? `blur(${backdropBlur}px)` : undefined;
}

function buildBoxShadow(input: {
  input: BuildContainerStyleInput;
  values: ContainerStyleValues;
}): string | undefined {
  const shadowParts = [
    input.values.finalBoxShadow,
    buildInnerBorderShadow(input),
    buildGlowShadow(input),
    ...buildGlossyShadows(input.input.glossy),
  ].filter(isPresentString);

  return shadowParts.length > 0 ? shadowParts.join(', ') : undefined;
}

function buildInnerBorderShadow(input: {
  input: BuildContainerStyleInput;
  values: ContainerStyleValues;
}): string | undefined {
  const { values } = input;
  const finalBorderColor = values.finalBorderColor;

  if (!finalBorderColor || !shouldBuildInnerBorderShadow(input)) {
    return undefined;
  }

  return `inset 0 0 0 ${values.finalBorderWidth}px ${getColorValue(
    finalBorderColor,
    values.finalBorderOpacity ?? 1,
  )}`;
}

function shouldBuildInnerBorderShadow(input: {
  input: BuildContainerStyleInput;
  values: ContainerStyleValues;
}): boolean {
  return [
    !input.input.useGradientBorder,
    input.input.innerBorder,
    input.values.finalBorderWidth > 0,
    input.values.finalBorderColor != null,
  ].every(Boolean);
}

function buildGlowShadow(input: {
  input: BuildContainerStyleInput;
  values: ContainerStyleValues;
}): string | undefined {
  const { values } = input;

  if (values.finalGlow <= 0 || !input.input.glowColor) {
    return undefined;
  }

  const glowOpacity = GLOW_OPACITY_MULTIPLIER * values.finalGlow;
  const glowColorValue =
    input.input.glowColor === ColorType.Accent
      ? getAccentColor(glowOpacity)
      : getColorValue(input.input.glowColor, glowOpacity);

  return [
    `0 ${GLOW_VERTICAL_OFFSET_MULTIPLIER * values.finalGlow}px`,
    `${GLOW_BLUR_MULTIPLIER * values.finalGlow}px`,
    `${GLOW_SPREAD_MULTIPLIER * values.finalGlow}px`,
    glowColorValue,
  ].join(' ');
}

function buildGlossyShadows(glossy: number): string[] {
  if (glossy <= 0) {
    return [];
  }

  return [
    [
      `rgba(255, 255, 255, ${glossy * UPPER_GLOSS_OPACITY_MULTIPLIER})`,
      `${glossy * UPPER_GLOSS_X_MULTIPLIER}px`,
      `${glossy * UPPER_GLOSS_Y_MULTIPLIER}px`,
      `${glossy * UPPER_GLOSS_BLUR_MULTIPLIER}px`,
      '0px inset',
    ].join(' '),
    [
      `rgba(255, 255, 255, ${LOWER_GLOSS_OPACITY})`,
      `${glossy * LOWER_GLOSS_X_MULTIPLIER}px`,
      `${glossy * LOWER_GLOSS_Y_MULTIPLIER}px`,
      `${glossy * LOWER_GLOSS_BLUR_MULTIPLIER}px`,
      '0px inset',
    ].join(' '),
  ];
}

function isPresentString(value: string | null | undefined): value is string {
  return Boolean(value);
}

function buildTransition(input: {
  smooth: boolean;
  animationDuration: number;
}): string | undefined {
  if (!input.smooth) {
    return undefined;
  }

  return TRANSITION_PROPERTIES
    .map((property) => `${property} ${input.animationDuration}ms ease-in-out`)
    .join(', ');
}

function resolveRootPosition(input: BuildContainerStyleInput): CSSProperties['position'] {
  return input.useGradientBorder || input.isLoadingActive || input.hasGlowLayer ? RELATIVE_POSITION : undefined;
}

function buildClickableStyle(input: BuildContainerStyleInput): Pick<
  CSSProperties,
  'cursor' | 'userSelect' | 'WebkitTapHighlightColor' | 'touchAction'
> {
  const clickable = isContainerClickable(input);

  return {
    cursor: resolveClickableValue(clickable, POINTER_CURSOR),
    userSelect: resolveClickableValue(clickable, DISABLED_USER_SELECT),
    WebkitTapHighlightColor: TRANSPARENT_TAP_HIGHLIGHT_COLOR,
    touchAction: resolveClickableValue(clickable, TOUCH_ACTION_MANIPULATION),
  };
}

function hasPaddingConflictLonghand(style: CSSProperties): boolean {
  return PADDING_CONFLICT_LONGHAND_KEYS.some((key: PaddingConflictLonghandKey) =>
    isPresentCssValue(style[key]),
  );
}

function splitTopLevelCssValues(value: string): string[] {
  const parts: string[] = [];
  let parenDepth = 0;
  let currentPart = '';

  for (const character of value) {
    if (character === '(') {
      parenDepth += 1;
    }
    if (character === ')') {
      parenDepth -= 1;
    }
    if (/\s/.test(character) && parenDepth === 0) {
      if (currentPart) {
        parts.push(currentPart);
        currentPart = '';
      }
      continue;
    }
    currentPart += character;
  }

  if (currentPart) {
    parts.push(currentPart);
  }

  return parts;
}

function expandPaddingBoxValue(
  padding: CSSProperties['padding'],
): Pick<CSSProperties, PaddingConflictLonghandKey> | undefined {
  if (typeof padding === 'number') {
    return { paddingTop: padding, paddingRight: padding, paddingBottom: padding, paddingLeft: padding };
  }

  if (typeof padding !== 'string') {
    return undefined;
  }

  const parts = splitTopLevelCssValues(padding.trim());
  const [first, second, third, fourth] = parts;

  const expansionByPartCount: Record<number, Pick<CSSProperties, PaddingConflictLonghandKey>> = {
    1: { paddingTop: first, paddingRight: first, paddingBottom: first, paddingLeft: first },
    2: { paddingTop: first, paddingRight: second, paddingBottom: first, paddingLeft: second },
    3: { paddingTop: first, paddingRight: second, paddingBottom: third, paddingLeft: second },
    4: { paddingTop: first, paddingRight: second, paddingBottom: third, paddingLeft: fourth },
  };

  return expansionByPartCount[parts.length];
}

// Mixing the `padding` shorthand with a longhand (e.g. a caller passing
// `style={{ paddingRight }}` over the container's own `padding`) makes React
// warn on rerender because per-property style diffing applies the two in
// undefined order. Expand the shorthand so caller longhands override cleanly.
function hasBorderConflictLonghand(style: CSSProperties): boolean {
  return BORDER_CONFLICT_LONGHAND_KEYS.some((key: BorderConflictLonghandKey) =>
    isPresentCssValue(style[key]),
  );
}

// Mixing the `border` shorthand with border longhands (e.g. a caller passing
// `style={{ borderColor }}` over the container's own `border`) makes React
// warn on rerender — same contract as normalizePaddingShorthand.
function normalizeBorderShorthand(style: CSSProperties): CSSProperties {
  if (!isPresentCssValue(style.border) || !hasBorderConflictLonghand(style)) {
    return style;
  }

  const { border: _border, ...styleWithoutBorder } = style;
  return styleWithoutBorder;
}

function normalizePaddingShorthand(style: CSSProperties): CSSProperties {
  if (!isPresentCssValue(style.padding) || !hasPaddingConflictLonghand(style)) {
    return style;
  }

  const { padding, ...styleWithoutPadding } = style;
  const expandedPadding = expandPaddingBoxValue(padding);

  if (!expandedPadding) {
    return style;
  }

  return { ...expandedPadding, ...styleWithoutPadding };
}

function isContainerClickable(input: BuildContainerStyleInput): boolean {
  return Boolean(input.onClick && input.clickable);
}

function resolveClickableValue<T>(clickable: boolean, value: T): T | undefined {
  return clickable ? value : undefined;
}

function buildMaskedBorderLayerStyle(input: {
  padding: string;
  backgroundImage: string;
  zIndex: number;
}): CSSProperties {
  return {
    content: '""',
    position: 'absolute',
    inset: 0,
    borderRadius: INHERITED_BORDER_RADIUS,
    padding: input.padding,
    backgroundImage: input.backgroundImage,
    WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
    WebkitMaskComposite: GRADIENT_MASK_COMPOSITE,
    maskImage: 'linear-gradient(#fff 0 0), linear-gradient(#fff 0 0)',
    maskClip: 'content-box, border-box',
    maskComposite: EXCLUDED_MASK_COMPOSITE,
    pointerEvents: 'none',
    zIndex: input.zIndex,
  };
}
